import {
  CompositionNote,
  type CompositionNoteListShard,
} from '@seen/core/models/compositionNote'
import { getSearchKey, normalizeQuery } from '@seen/core/search'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

interface UseNotesPageShardSearchResult {
  data: CompositionNote[]
  isLoading: boolean
  isPending: boolean
  isError: boolean
  failedShardCount: number
}

export function useNotesPageShardSearch(
  shards: CompositionNoteListShard[]
): UseNotesPageShardSearchResult {
  const medplum = useMedplum()

  const results = useQueries({
    queries: shards.map((shard) => {
      const parameters = normalizeQuery(shard.query)
      const boundsKey = `min:${shard.minInclusive ?? ''}|max:${shard.maxExclusive ?? ''}`
      return {
        queryKey: cacheKeys.searchResourcePages(
          CompositionNote.RESOURCE_TYPE,
          `${getSearchKey(medplum, CompositionNote.RESOURCE_TYPE, parameters)}|${boundsKey}`
        ),
        queryFn: async () => {
          const byId = new Map<string, CompositionNote>()
          for await (const page of medplum.searchResourceClassPages(
            CompositionNote,
            shard.query
          )) {
            for (const note of page) {
              if (
                shard.minInclusive &&
                !CompositionNote.noteOnOrAfterDay(note, shard.minInclusive)
              ) {
                continue
              }
              if (
                shard.maxExclusive &&
                !CompositionNote.noteBeforeDay(note, shard.maxExclusive)
              ) {
                continue
              }
              if (!byId.has(note.id)) {
                byId.set(note.id, note)
              }
            }
          }
          return [...byId.values()]
        },
      }
    }),
  })

  const isLoading = results.some((r) => r.isLoading)
  const isPending = results.some((r) => r.isPending)
  const failedShardCount = results.filter((r) => r.isError).length
  const isError = failedShardCount > 0
  const fingerprint = results.map((r) => r.dataUpdatedAt).join(',')
  const data = useMemo(
    () => results.flatMap((r) => r.data ?? []),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- results identity is unstable; the fingerprint captures when any shard's data changed
    [fingerprint]
  )

  const error = results.find((r) => r.error)?.error
  useErrorToast(
    error &&
      !(error instanceof Error && error.name === 'AbortError') &&
      `Error loading notes: ${error.message ?? 'Unknown error'}`
  )

  return { data, isLoading, isPending, isError, failedShardCount }
}
